export type BannerPlacement =
  | "top_strip"
  | "home_below_hero"
  | "home_between_blocks"
  | "account_invite"
  | "account_ambassador_slide"
  | "shop_promo"
  | "product_promo"
  | "anonymous_toast";

export type BannerAudience =
  | "all"
  | "anonymous"
  | "authenticated"
  | "lover"
  | "ambassador"
  | "super_star";

export type BannerSize = "compact" | "default" | "tall";

export type BannerMarket = "all" | "SA" | "AE" | "KW" | "QA" | "BH" | "OM" | "IQ" | "US" | "ROW";

export type BannerTriggerType =
  | "none"
  | "wishlist_contains"
  | "cart_returning_visitor"
  | "from_abandoned_cart_link"
  | "cart_or_wishlist";

export interface BannerRow {
  id: string;
  placement: BannerPlacement;
  audience: BannerAudience;
  size: BannerSize;
  campaign: string | null;
  market: BannerMarket;
  trigger_type: BannerTriggerType;
  trigger_value: string | null;
  title_ar: string;
  title_en: string;
  body_ar: string;
  body_en: string;
  cta_label_ar: string;
  cta_label_en: string;
  cta_link: string;
  image_url: string | null;
  min_completed_orders: number;
  delay_seconds: number;
  enabled: boolean;
  starts_at: string | null;
  ends_at: string | null;
  sort_order: number;
  display_mode: "inline" | "story";
  created_at: string;
  updated_at: string;
}

export interface BannerStatRow {
  banner_id: string;
  views: number;
  clicks: number;
  ctr: number;
}

export const BANNER_DISPLAY_MODE_VALUES = ["inline", "story"] as const;

export const BANNER_TRIGGER_VALUES = [
  "none",
  "wishlist_contains",
  "cart_returning_visitor",
  "from_abandoned_cart_link",
  "cart_or_wishlist",
] as const;

export const BANNER_PLACEMENT_VALUES = [
  "top_strip",
  "home_below_hero",
  "home_between_blocks",
  "account_invite",
  "account_ambassador_slide",
  "shop_promo",
  "product_promo",
  "anonymous_toast",
] as const;

export const BANNER_AUDIENCE_VALUES = [
  "all",
  "anonymous",
  "authenticated",
  "lover",
  "ambassador",
  "super_star",
] as const;

export const BANNER_SIZE_VALUES = ["compact", "default", "tall"] as const;

export const BANNER_MARKET_VALUES = ["all", "SA", "AE", "KW", "QA", "BH", "OM", "IQ", "US", "ROW"] as const;

/**
 * Banners only ever carry the 10 markets above: the admin editor offers exactly
 * these, and the public `/api/banners` GET validator rejects anything else with
 * a 400 + empty list. Storefront markets now include ~68 added shipping
 * countries (GB, DE, FR, CA, BR, …) that are not banner markets, so any market
 * outside this set falls back to ROW — the same bucket those visitors resolved
 * to before those countries became supported markets.
 */
export function toBannerMarket(market: string | null | undefined): BannerMarket {
  return (BANNER_MARKET_VALUES as readonly string[]).includes(market ?? "")
    ? (market as BannerMarket)
    : "ROW";
}


export const BANNER_COLS =
  "id, placement, audience, size, campaign, market, trigger_type, trigger_value, title_ar, title_en, body_ar, body_en, cta_label_ar, cta_label_en, cta_link, image_url, min_completed_orders, delay_seconds, enabled, starts_at, ends_at, sort_order, display_mode, created_at, updated_at";